import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Building, Eye, FileText, Folder, Users } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import JournalOrganization from "@/components/forms/summary/organize-journal"
import {
  ResourceDetailModal,
  type ResourceDisplay,
} from "@/components/modals/resource-detail-modal"
import { Page } from "@/components/page"
import { PageLoader } from "@/components/page-loader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/_individual/personal/journal-management")({
  component: JournalManagementPage,
})

function JournalManagementPage() {
  const { data: user, isLoading: userLoading } = trpc.users.me.useQuery()
  const estateId = user?.estateId ?? ""

  if (userLoading) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Journal Management
          </Typography>
        }
      >
        <PageLoader />
      </Page>
    )
  }

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Journal Management
        </Typography>
      }
    >
      <div className="space-y-8">
        {/* Shared Journals */}
        <SharedJournals />

        {/* My Journals */}
        <div className="rounded-lg border p-6">
          <JournalOrganization estateId={estateId} />
        </div>
      </div>
    </Page>
  )
}

function SharedJournals() {
  const { t } = useTranslation()
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(null)

  const { data: sharedJournals = [], isLoading } = trpc.journals.listMySharedJournals.useQuery()

  const { data: journalResources, isLoading: resourcesLoading } =
    trpc.journals.listResource.useQuery(
      { journalId: selectedJournalId ?? "" },
      { enabled: !!selectedJournalId },
    )

  const selectedJournal = sharedJournals.find((j) => j.id === selectedJournalId)

  if (selectedJournalId && selectedJournal) {
    return (
      <div className="rounded-lg border p-6">
        <div className="bg-muted text-muted-foreground px-4 py-2 rounded-t-lg mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedJournalId(null)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Shared Journals
            </Button>
            <span className="text-sm">•</span>
            <h2 className="text-lg font-medium">{selectedJournal.name}</h2>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Folder className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <Typography as="h2" className="text-2xl font-semibold">
                {selectedJournal.name}
              </Typography>
              <Typography as="p" className="text-muted-foreground capitalize">
                {t("components.journal.enums.journalType.option", {
                  context: selectedJournal.journalType,
                })}
                {selectedJournal.journalPriority && ` • ${selectedJournal.journalPriority}`}
              </Typography>
              {selectedJournal.estateName && (
                <Typography as="p" className="text-sm text-muted-foreground">
                  Estate: {selectedJournal.estateName}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Read-only Resources */}
        <Card>
          <CardHeader>
            <CardTitle>
              <Typography as="h3" size="lg">
                Journal Resources
              </Typography>
            </CardTitle>
            <Typography as="p" className="text-muted-foreground">
              Resources linked to this journal ({journalResources?.length ?? 0} items)
            </Typography>
          </CardHeader>
          <CardContent>
            <ReadOnlyResourceList resources={journalResources} isLoading={resourcesLoading} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="rounded-lg border p-6">
      <div className="mb-4">
        <Typography as="h2" size="lg">
          Shared Journals
        </Typography>
        <Typography as="p" size="sm" className="text-muted-foreground mt-1">
          Journals shared with you by other users. These are view-only.
        </Typography>
      </div>

      {isLoading && <PageLoader />}

      {!isLoading && sharedJournals.length === 0 && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Folder className="w-8 h-8 text-muted-foreground" />
          </div>
          <Typography as="h3" size="lg" className="mb-2">
            No shared journals yet
          </Typography>
          <Typography as="p" className="text-muted-foreground">
            When someone shares a journal with you, it will appear here.
          </Typography>
        </div>
      )}

      {!isLoading && sharedJournals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sharedJournals.map((journal) => (
            <Card
              key={journal.id}
              className="hover:shadow-md transition-shadow flex flex-col justify-between"
            >
              <CardContent className="p-4 flex flex-col justify-between flex-1">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
                      <Folder className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-card-foreground">{journal.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t("components.journal.enums.journalType.option", {
                          context: journal.journalType,
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 mb-4 text-sm text-muted-foreground">
                    {journal.estateName && (
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full" />
                        Estate: {journal.estateName}
                      </div>
                    )}
                    {journal.journalPriority && (
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full" />
                        Priority: {journal.journalPriority}
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  size="sm"
                  onClick={() => setSelectedJournalId(journal.id)}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function getResourceDisplayInfo(resource: ResourceDisplay) {
  switch (resource.type) {
    case "contact":
      return resource.email || resource.phoneNumber || "Contact"
    case "asset":
      return resource.currentValue ? `$${resource.currentValue}` : "Asset"
    case "document":
      return resource.description || "Document"
    case "company":
      return resource.companyDetails || "Company"
    case "task":
      return resource.dueDate ? `Due: ${new Date(resource.dueDate).toLocaleDateString()}` : "Task"
    default:
      return ""
  }
}

function getResourceDisplayName(resource: ResourceDisplay) {
  if (resource.type === "asset") return resource.name
  if (resource.type === "contact")
    return `${resource.firstName ?? ""} ${resource.lastName ?? ""}`.trim()
  if (resource.type === "company") return resource.companyName || resource.name
  return resource.title || resource.name
}

function getResourceIcon(type: string) {
  switch (type) {
    case "contact":
      return <Users className="w-4 h-4 text-primary" />
    case "asset":
      return <Building className="w-4 h-4 text-primary" />
    case "document":
      return <FileText className="w-4 h-4 text-primary" />
    default:
      return <Folder className="w-4 h-4 text-primary" />
  }
}

function ReadOnlyResourceList({
  resources,
  isLoading,
}: {
  resources: unknown[] | undefined
  isLoading: boolean
}) {
  const [selectedResource, setSelectedResource] = useState<ResourceDisplay | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <Typography as="p" className="text-muted-foreground">
          Loading resources...
        </Typography>
      </div>
    )
  }

  const validResources = (resources ?? []).filter(
    (r): r is ResourceDisplay => r != null && typeof r === "object" && "type" in r,
  )

  if (validResources.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Folder className="w-8 h-8 text-muted-foreground" />
        </div>
        <Typography as="h3" size="lg" className="mb-2">
          No resources linked
        </Typography>
        <Typography as="p" className="text-muted-foreground">
          This journal has no linked resources.
        </Typography>
      </div>
    )
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {validResources.map((resource) => (
          <Card
            key={resource.journalResourceId}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => {
              setSelectedResource(resource)
              setDetailModalOpen(true)
            }}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded">{getResourceIcon(resource.type)}</div>
              <div className="flex-1 min-w-0">
                <Typography as="h3" className="font-medium truncate">
                  {getResourceDisplayName(resource)}
                </Typography>
                <Typography as="p" className="text-sm text-muted-foreground capitalize">
                  {resource.type}
                </Typography>
                <Typography as="p" className="text-xs text-muted-foreground mt-1">
                  {getResourceDisplayInfo(resource)}
                </Typography>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <ResourceDetailModal
        open={detailModalOpen}
        setOpen={setDetailModalOpen}
        resource={selectedResource}
      />
    </>
  )
}
